import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { normalizePathname, resolveActive, routeAccess } from "@bw/shared/navigation";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link, Navigate, Outlet, useLocation } from "react-router-dom";

import { UNLOCKED_ORG_STATUSES } from "@/app/org-access-policy";
import { businessNavigation } from "@/config/modes";
import { useMyOrg } from "@/hooks/use-my-org";

import { useBusinessNavAccess } from "./useBusinessNavAccess";

/** Business setup routes itself (owners of a live org go to Home). */
const SELF_ROUTING_PAGES = new Set(["business-setup"]);

/**
 * Routes whose authority is not a facility action: tournament officials
 * (umpire, referee, marshal) hold check-in-only facility roles and reach these
 * through their tournament official assignment. The pages' own guards and the
 * server decide; the menu's rules do not apply.
 */
const NON_NAV_AUTHORITY_ROUTES = [
  /^\/tournaments\/[^/]+\/control-center\/?$/,
  /^\/tournaments\/[^/]+\/review-order-of-play\/?$/,
];

/** Fails closed when a visibility input failed, with a way to ask again. */
export function AccessCheckFailedState({ onRetry }: { onRetry: () => void }) {
  return (
    <Box sx={{ display: "grid", placeItems: "center", px: 2, py: { xs: 4, sm: 8 } }}>
      <Paper variant="outlined" sx={{ width: "100%", maxWidth: 520, p: { xs: 3, sm: 4 } }}>
        <Stack spacing={1.5} sx={{ alignItems: "center", textAlign: "center" }}>
          <LockOutlinedIcon aria-hidden="true" sx={{ fontSize: 32, color: "text.secondary" }} />
          <Typography component="h1" variant="h5" sx={{ fontWeight: 700 }}>
            We couldn’t check your access to this page
          </Typography>
          <Typography color="text.secondary">
            Your permissions didn’t load. Try again in a moment.
          </Typography>
          <Button variant="contained" onClick={onRetry} sx={{ mt: 1 }}>
            Try again
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}

/** The in-shell state for a page the menu hides from this person. */
export function NoAccessState({ landing }: { landing: string | null }) {
  return (
    <Box sx={{ display: "grid", placeItems: "center", px: 2, py: { xs: 4, sm: 8 } }}>
      <Paper variant="outlined" sx={{ width: "100%", maxWidth: 520, p: { xs: 3, sm: 4 } }}>
        <Stack spacing={1.5} sx={{ alignItems: "center", textAlign: "center" }}>
          <LockOutlinedIcon aria-hidden="true" sx={{ fontSize: 32, color: "text.secondary" }} />
          <Typography component="h1" variant="h5" sx={{ fontWeight: 700 }}>
            You don’t have access to this page
          </Typography>
          <Typography color="text.secondary">
            Your role at this club doesn’t include it. Ask the club owner if you need it.
          </Typography>
          {landing ? (
            <Button component={Link} to={landing} variant="contained" sx={{ mt: 1 }}>
              Go to your start page
            </Button>
          ) : null}
        </Stack>
      </Paper>
    </Box>
  );
}

/**
 * One gate for every shell route, driven by the navigation model: a route that
 * belongs to a page, section or user-menu page this person cannot see renders
 * the no-access state (its data and actions never mount); detail routes follow
 * their owning page; a hidden section of a visible page opens the page's first
 * visible section. URLs no page claims keep their own guards, and before the
 * organization is approved, denials that only come from its status are left
 * to the approval guards. The server stays authoritative on every endpoint.
 */
export function NavRouteGate() {
  const {
    allow,
    allowWhenApproved,
    allowIfFailedInputsGranted,
    ready,
    financeReady,
    coachReady,
    accessCheckFailed,
    retryAccess,
    organizationOwner,
    landing,
  } = useBusinessNavAccess();
  const orgStatus = useMyOrg().org?.status;
  const { pathname, search } = useLocation();
  const normalized = normalizePathname(pathname);
  if (NON_NAV_AUTHORITY_ROUTES.some((route) => route.test(normalized))) return <Outlet />;
  const target = resolveActive(businessNavigation.modes, pathname, search);
  // URLs no page claims, and pages that route themselves, need no hints.
  if (!target || SELF_ROUTING_PAGES.has(target.page.id)) return <Outlet />;
  // Decide only once every visibility input has answered (permissions, finance
  // access, coach profile). Answers stay settled while they refetch.
  if (!ready || financeReady === false || coachReady === false) {
    return (
      <Box role="status" aria-label="Checking access" sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  const access = routeAccess(businessNavigation, allow, pathname, search);
  if (access.kind === "allow") return <Outlet />;

  const orgApproved = Boolean(orgStatus && UNLOCKED_ORG_STATUSES.has(orgStatus));
  if (!orgApproved) {
    // Only denials that approval would lift go to the approval guards; a
    // permission denial stays a denial.
    const whenApproved = routeAccess(businessNavigation, allowWhenApproved, pathname, search);
    if (whenApproved.kind === "allow") return <Outlet />;
    // The owner finishes Stripe onboarding on Payments before finance read exists.
    if (target.page.id === "payments" && organizationOwner) return <Outlet />;
  }

  // A denial that a failed permission read could have lifted is unconfirmed:
  // still closed, but offer to ask again instead of a settled no-access. A
  // failure that cannot change this route's answer does not matter here.
  if (
    accessCheckFailed &&
    routeAccess(businessNavigation, allowIfFailedInputsGranted, pathname, search).kind === "allow"
  ) {
    return <AccessCheckFailedState onRetry={retryAccess} />;
  }
  if (access.kind === "redirect") return <Navigate to={access.to} replace />;
  return <NoAccessState landing={landing} />;
}
